export interface TooltipOptions {
  text: string
  placement?: 'top' | 'bottom' | 'start' | 'end'
}

/**
 * Attaches a tooltip to an element. Uses logical placement names
 * (start/end) instead of left/right so behavior is correct in RTL.
 */
export function attachTooltip(target: HTMLElement, options: TooltipOptions): () => void {
  let tip: HTMLElement | null = null
  const placement = options.placement ?? 'top'

  function show(): void {
    tip = document.createElement('div')
    tip.className = `sn-tooltip sn-tooltip--${placement}`
    tip.setAttribute('role', 'tooltip')
    tip.textContent = options.text
    document.body.appendChild(tip)

    const targetRect = target.getBoundingClientRect()
    const tipRect = tip.getBoundingClientRect()

    let top = targetRect.top
    let left = targetRect.left + targetRect.width / 2 - tipRect.width / 2

    if (placement === 'top')
      top = targetRect.top - tipRect.height - 8
    if (placement === 'bottom')
      top = targetRect.bottom + 8
    if (placement === 'start' || placement === 'end') {
      top = targetRect.top + targetRect.height / 2 - tipRect.height / 2
      const dir = getComputedStyle(document.documentElement).direction
      const onInlineStart = (placement === 'start' && dir === 'ltr') || (placement === 'end' && dir === 'rtl')
      left = onInlineStart ? targetRect.left - tipRect.width - 8 : targetRect.right + 8
    }

    tip.style.top = `${top + window.scrollY}px`
    tip.style.left = `${left + window.scrollX}px`
    requestAnimationFrame(() => tip?.classList.add('sn-tooltip--visible'))
  }

  function hide(): void {
    tip?.remove()
    tip = null
  }

  target.addEventListener('mouseenter', show)
  target.addEventListener('mouseleave', hide)
  target.addEventListener('focus', show)
  target.addEventListener('blur', hide)

  return () => {
    target.removeEventListener('mouseenter', show)
    target.removeEventListener('mouseleave', hide)
    target.removeEventListener('focus', show)
    target.removeEventListener('blur', hide)
    hide()
  }
}
